import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";

interface AdAccount {
  id: string;
  organizationId: string;
  organizationName: string | null;
  metaAccountId: string;
  name: string;
}

interface Props {
  toOrgId: string;
  toOrgName: string;
  onClose: () => void;
}

/**
 * Transfer an existing ad_account from any other org INTO `toOrgId`.
 * Lists all ad_accounts the SUPER_ADMIN can see, filters out those already in toOrgId.
 */
export default function TransferAdAccountDialog({ toOrgId, toOrgName, onClose }: Props) {
  const qc = useQueryClient();
  const [pick, setPick] = useState<string>("");
  const [q, setQ] = useState("");

  const all = useQuery({
    queryKey: ["ad-accounts"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/ad-accounts");
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as AdAccount[];
    },
  });

  const candidates = useMemo(() => {
    const rows = all.data ?? [];
    const needle = q.trim().toLowerCase();
    return rows
      .filter((a) => a.organizationId !== toOrgId)
      .filter((a) => !needle ||
        ((a.name ?? "") + " " + a.metaAccountId + " " + (a.organizationName ?? "")).toLowerCase().includes(needle));
  }, [all.data, toOrgId, q]);

  const transfer = useMutation({
    mutationFn: async () => {
      if (!pick) throw new Error("请选择一个广告账户");
      const { error, response } = await api.PATCH(
        "/api/v1/ad-accounts/{id}/organization" as never,
        { params: { path: { id: pick } }, body: { organizationId: toOrgId } } as never
      );
      if (error || !response.ok) throw error || new Error("转移失败");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ad-accounts"] });
      toast.success("已转入");
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-background rounded-lg border border-border w-full max-w-2xl shadow-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold">转入广告账户 → {toOrgName}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>

        <div className="p-5 space-y-3 overflow-auto flex-1">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜索账户名 / act_id / 原公司"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />

          {all.isLoading && <div className="text-sm text-muted-foreground">加载中…</div>}
          {!all.isLoading && candidates.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-6">
              没有其他公司的广告账户可转入
            </div>
          )}

          <div className="space-y-1.5">
            {candidates.map((a) => (
              <label
                key={a.id}
                className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer ${
                  pick === a.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                }`}
              >
                <input
                  type="radio"
                  checked={pick === a.id}
                  onChange={() => setPick(a.id)}
                  name="transfer-pick"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{a.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    ACT_{a.metaAccountId} · 当前在 {a.organizationName ?? "—"}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
          >
            取消
          </button>
          <button
            onClick={() => transfer.mutate()}
            disabled={transfer.isPending || !pick}
            className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {transfer.isPending ? "转移中…" : "确认转入"}
          </button>
        </div>
      </div>
    </div>
  );
}
